import PropTypes from 'prop-types';

/**
 * Title component using Preline/Tailwind typography
 *
 * A flexible heading component that renders h1-h6 based on the level prop.
 * Uses Preline's typography classes for consistent styling across the app.
 *
 * @param {Object} props
 * @param {number} props.level - Heading level (1-6)
 * @param {string} [props.className] - Additional CSS classes
 * @param {React.ReactNode} props.children - Content to display
 * @param {Object} [props...rest] - Any other HTML heading attributes
 *
 * @example
 * <Title level={1}>Main Page Title</Title>
 * <Title level={2} className="mb-4">Section Title</Title>
 */
export function Title({ level = 1, className = '', children, ...rest }) {
  // Map heading levels to Preline/Tailwind typography classes
  const baseClasses = {
    1: 'text-2xl font-bold text-default',
    2: 'text-xl font-bold text-default',
    3: 'text-lg font-bold text-default',
    4: 'text-base font-bold text-default',
    5: 'text-base font-bold text-default',
    6: 'text-base font-bold text-default',
  };

  // Validate level
  const validLevel = Math.min(Math.max(level, 1), 6);
  const classes = `${baseClasses[validLevel]} ${className}`.trim();

  // Dynamically create the heading element
  const Tag = `h${validLevel}`;

  return (
    <Tag className={classes} {...rest}>
      {children}
    </Tag>
  );
}

Title.propTypes = {
  level: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
};
